import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'

import { fontFamilyOpenSans, fontSize2XSmall, fontSize4XSmall, fontWeightRegular, black, fontSizeXSmall } from '../../constants/principles'
import { white, blueBase, grayBase, grayLight20, blueLight20, redLight10, grayDark, argoRed, argoBlue, argoDisabled, argoText, argoInputBorder } from '../../constants/principles'
import { useToggler } from '../../hooks'
import Triangle from './ExclamationTriangle.js'
import Padlock from './Padlock.js'

export default ({
  children,
  label,
  placeholder = label,
  value,
  inputRef,
  backgroundColor,
  onChange,
  onBlur,
  fullWidth = false,
  isError,
  id,
  disabled = false,
  maxLength,
  type,
  formatMask = v => v,
  unformatMask = v => v,
  autoFocus,
  pattern,
  inputMode,
  testId,
  maskData = false,
  onClick = () => { },
  useArgoStyle
}) => {
  const [hasFocus, toggleFocus] = useToggler(false)
  const [localValue, setLocalValue] = useState(value)
  autoFocus = false // shut off until we can figure out when/where we want it
  useEffect(() => {
    setLocalValue(value)
  }, [value])

  return (
    <>
      <StyledDiv hasFocus={hasFocus} fullWidth={fullWidth} isError={isError} disabled={disabled} useArgoStyle={useArgoStyle}>
        <StyledLabel htmlFor={id} showLabel={hasFocus || localValue} hasFocus={hasFocus} backgroundColor={backgroundColor} disabled={disabled} isError={isError} useArgoStyle={useArgoStyle} style={{ top: useArgoStyle && (hasFocus || localValue ? 5 : 16) }}>
          {useArgoStyle ? label : (hasFocus || localValue) && label}
        </StyledLabel>
        <InputDiv placeholderLength={placeholder.length} useArgoStyle={useArgoStyle}>
          <StyledInput
            data-cs-mask={maskData}
            type={type || 'text'}
            id={id}
            autoComplete="on"
            data-testid={testId}
            min="0"
            value={formatMask(localValue)}
            ref={inputRef}
            placeholder={!hasFocus && !useArgoStyle && placeholder}
            disabled={disabled}
            onFocus={toggleFocus}
            onBlur={() => {
              toggleFocus()
              onBlur && onBlur(unformatMask(localValue))
            }}
            onChange={e => {
              const unformattedValue = unformatMask(e.target.value)
              onChange && onChange(unformattedValue)
              setLocalValue(unformattedValue)
            }}
            onClick={e => onClick(e)}
            maxLength={maxLength}
            autoFocus={autoFocus}
            spellCheck={false}
            inputMode={inputMode || ''}
            pattern={pattern || '.*'}
            useArgoStyle={useArgoStyle}
            isError={isError}
          />
          {!useArgoStyle && (
            <>
              {isError && (
                <StyledIcon>
                  <Triangle colorFill={redLight10} height="14px" width="14px" />
                </StyledIcon>
              )}
              {disabled && (
                <StyledIcon>
                  <Padlock colorFill={grayLight20} height="16px" width="16px" />
                </StyledIcon>
              )}
              {children}
            </>
          )}
        </InputDiv>
      </StyledDiv>
    </>
  )
}
const StyledIcon = styled.div`
  display: flex;
  align-items: center;
  padding-right: 4px;
`
const StyledDiv = styled.div`
  font-family: ${fontFamilyOpenSans};
  border: 1px ${p => (p.useArgoStyle ? 'none' : 'solid')};
  width: ${p => p.fullWidth && 100}%;

  ${p =>
    !p.useArgoStyle
      ? `
    line-height: 0.1em;
    text-align: left;
    height: 42px;
    border-color: ${p.hasFocus ? blueBase : grayBase};
    border-color: ${p.isError && redLight10};
    border-color: ${p.disabled && grayLight20};
    `
      : `
    display: flex;
    position: relative;
    flex-basis: 100%
  `}
`
const InputDiv = styled.div`
  display: flex;
  flex-direction: row;
  overflow: hidden;
  min-width: calc(${p => p.placeholderLength} * 8px);
  padding-right: ${p => !p.useArgoStyle && '4px'};
  width: ${p => p.useArgoStyle && '100%'};
`
const StyledInput = styled.input`
  color: ${p => (p.useArgoStyle ? argoText : grayDark)};
  width: 100%;
  font-size: ${p => (p.useArgoStyle ? fontSizeXSmall : fontSize2XSmall)};
  padding: ${p => (p.useArgoStyle ? '16px 12px 8px 16px' : '6px 4px')};
  border: ${p => (p.useArgoStyle ? `0.5px solid ${argoInputBorder}` : '1px none')};
  line-height: ${p => (p.useArgoStyle ? '1.375' : 'normal')};

  ${p =>
    !p.useArgoStyle
      ? `
    display: block;
    margin: 1px 4px 3px 4px;
    outline: none;
    &::placeholder {
      color: ${grayDark};
    }
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
    }
    -moz-appearance: textfield;
    }
    `
      : `
    border-radius: 4px;
    border-color: ${p.isError && argoRed};
    background-color: ${white};
    letter-spacing: 0;
    max-width: 100%;
    outline: 2px solid transparent;
    outline-offset: 2px;
    resize: none;
    transition-duration: 0.2s;
    transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    :hover {
      border-color: #939393;
    }
    :focus {
      border-color: ${argoBlue};
    }
  `};

  :disabled {
    background-color: ${p => (p.useArgoStyle ? argoDisabled : white)};
    border-color: ${p => p.useArgoStyle && argoInputBorder};
    color: ${p => p.useArgoStyle && 'silver'};
  }
`

const StyledLabel = styled.label`
  font-size: ${p => (p.useArgoStyle ? (p.showLabel ? fontSize4XSmall : fontSize2XSmall) : fontSize4XSmall)};
  font-weight: ${fontWeightRegular};
  border-radius: ${p => (p.useArgoStyle ? '4px' : '3px')};
  position: ${p => (p.useArgoStyle ? 'absolute' : 'relative')};

  ${p =>
    !p.useArgoStyle
      ? `
    margin-left: 7px;
    padding: 0 4px;
    background-color: ${p.showLabel ? p.backgroundColor || white : `'transparent'`};
    color: ${p.hasFocus ? blueLight20 : black};
    color: ${p.disabled && grayLight20};
    display: inline;
    top: -3px;
    `
      : `
    color: ${p.hasFocus ? argoBlue : argoText};
    color: ${p.isError && !p.hasFocus && argoRed};
    color: ${p.disabled && 'silver'};

    inset: 16px;
    line-height: 16px;
    pointer-events: none;
    transform-origin: top left;
    transition-duration: 0.15s;
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  `};
`
